import { useEffect, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { useIncidentsFilterStore } from '../store/incidentsFilterStore'
import { INCIDENT_PRIORITIES, INCIDENT_STATUSES } from '../types/incident.types'
import type { IncidentPriority, IncidentStatus } from '../types/incident.types'
import { PRIORITY_LABELS, STATUS_LABELS } from '../constants/incidentMaps'

interface FilterDropdownProps<T extends string> {
  label: string
  options: T[]
  selected: T[]
  labels: Record<T, string>
  onToggle: (value: T) => void
}

function FilterDropdown<T extends string>({
  label,
  options,
  selected,
  labels,
  onToggle,
}: FilterDropdownProps<T>): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent): void {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const count = selected.length

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`inline-flex w-32 items-center justify-between rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition-colors ${
          count > 0
            ? 'border-brand-green text-gray-900'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {label}
        <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </button>
      {count > 0 && (
        <span className="absolute right-0 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-green px-1 text-[10px] font-bold text-white">
          {count}
        </span>
      )}
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[200px] space-y-0.5 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
          {options.map((opt) => {
            const isSelected = selected.includes(opt)
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onToggle(opt)}
                className={`flex w-full items-center rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  isSelected
                    ? 'border-brand-green bg-brand-green/5 text-gray-900'
                    : 'border-transparent text-gray-700 hover:bg-gray-50'
                }`}
              >
                {labels[opt]}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function FilterPill({ label, onRemove }: { label: string; onRemove: () => void }): JSX.Element {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="ml-0.5 text-gray-400 hover:text-gray-600"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

export function IncidentsHeader(): JSX.Element {
  const priorities = useIncidentsFilterStore((state) => state.priorities)
  const statuses = useIncidentsFilterStore((state) => state.statuses)
  const setPriorities = useIncidentsFilterStore((state) => state.setPriorities)
  const setStatuses = useIncidentsFilterStore((state) => state.setStatuses)
  const clearFilters = useIncidentsFilterStore((state) => state.clearFilters)

  const hasActive = priorities.length > 0 || statuses.length > 0

  function togglePriority(value: IncidentPriority): void {
    setPriorities(
      priorities.includes(value) ? priorities.filter((p) => p !== value) : [...priorities, value],
    )
  }

  function toggleStatus(value: IncidentStatus): void {
    setStatuses(
      statuses.includes(value) ? statuses.filter((s) => s !== value) : [...statuses, value],
    )
  }

  return (
    <header className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Incident Details</h1>
          <p className="mt-1 text-sm text-gray-500">Check the details of all the incidents</p>
        </div>

        <div className="flex shrink-0 gap-2">
          <FilterDropdown
            label="Priority"
            options={INCIDENT_PRIORITIES}
            selected={priorities}
            labels={PRIORITY_LABELS}
            onToggle={togglePriority}
          />
          <FilterDropdown
            label="Status"
            options={INCIDENT_STATUSES}
            selected={statuses}
            labels={STATUS_LABELS}
            onToggle={toggleStatus}
          />
        </div>
      </div>

      {hasActive && (
        <div className="flex flex-col items-end gap-1 self-end">
          <div className="flex w-full max-w-72 gap-2 overflow-x-auto pb-2 text-center [mask-image:linear-gradient(to_right,transparent,#000_16px,#000_calc(100%_-_16px),transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,#000_16px,#000_calc(100%_-_16px),transparent)]">
            {priorities.map((p) => (
              <FilterPill key={`priority-${p}`} label={PRIORITY_LABELS[p]} onRemove={() => togglePriority(p)} />
            ))}
            {statuses.map((s) => (
              <FilterPill key={`status-${s}`} label={STATUS_LABELS[s]} onRemove={() => toggleStatus(s)} />
            ))}
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            Clear all
          </button>
        </div>
      )}
    </header>
  )
}
